import React, { useState } from "react";
import Card, { Kicker } from "./Surface";
import { SKILLS } from "../../data/cv";
import { useMediaQuery } from "../../lib/useMediaQuery";
import { cn } from "../../lib/utils";

/**
 * The skills board.
 *
 * Two layouts, because four columns and one column are not the same idea at
 * different widths.
 *
 * **Desk** shows all four groups side by side. You can read the whole stack in
 * one look, which is what somebody scanning a CV for "does he know Spark" is
 * actually doing.
 *
 * **Phone** shows one group at a time behind a row of tabs. Stacked, the four
 * columns came to twenty-five entries of two lines each — roughly two and a
 * half screens of list, which nobody scrolls to the end of. One group is six
 * entries and fits on the screen it starts on.
 *
 * This is a real branch rather than `hidden desk:block` on two copies: the
 * point is to stop rendering the other nineteen items, not to paint them
 * offscreen.
 */
export default function Skills() {
  const desk = useMediaQuery("(min-width: 56.25rem)");
  const [open, setOpen] = useState(SKILLS[0]?.id);
  const active = SKILLS.find((g) => g.id === open) ?? SKILLS[0];

  return (
    <Card tone="solid" pad="lg">
      <Kicker>Waarmee</Kicker>
      <h2 className="max-w-[16ch]">Wat ik echt vastpak.</h2>
      <p className="mt-4 max-w-[54ch] text-[15px]">
        Geen percentages. Een balk die beweert dat ik React voor 87% ken is een getal dat niemand
        kan controleren — hoe vaak ik iets gebruik en waarvoor, dat kan je wel nagaan.
      </p>

      {desk ? (
        <div className="mt-10 grid gap-x-10 gap-y-11 desk:grid-cols-4">
          {SKILLS.map((group) => (
            <section key={group.id}>
              <Head group={group} />
              <List items={group.items} />
            </section>
          ))}
        </div>
      ) : (
        <>
          <div className="rail mt-8 gap-1.5 pb-1" role="tablist" aria-label="Vakgebieden">
            {SKILLS.map((group) => (
              <button
                key={group.id}
                type="button"
                role="tab"
                aria-selected={group.id === active?.id}
                onClick={() => setOpen(group.id)}
                className={cn(
                  "slab tap h-11 shrink-0 px-4 text-[12.5px] font-bold whitespace-nowrap",
                  "transition-colors duration-200",
                  group.id === active?.id
                    ? "bg-accent text-void"
                    : "border border-line bg-surface-solid text-muted",
                )}
              >
                <span>
                  {group.label}
                  <span className="kicker !text-current opacity-60">{group.items.length}</span>
                </span>
              </button>
            ))}
          </div>

          {active && (
            <section className="mt-7">
              <p className="text-[13.5px]">{active.line}</p>
              <List items={active.items} />
            </section>
          )}
        </>
      )}
    </Card>
  );
}

function Head({ group }) {
  return (
    <>
      <h3 className="flex items-baseline gap-2 font-display text-[15px] leading-tight font-medium tracking-[-0.04em]">
        {group.label}
        <span className="kicker">{group.items.length}</span>
      </h3>
      <p className="mt-2 text-[12.5px] leading-[1.5]">{group.line}</p>
    </>
  );
}

/**
 * Name over note, never name beside note. Side by side, a mono note at this
 * tracking is wider than the column it sits in and spills into the next one.
 */
function List({ items }) {
  return (
    <ul className="mt-5 border-t border-hair">
      {items.map((item) => (
        <li
          key={item.name}
          className="border-b border-hair py-3 transition-colors duration-200 hover:border-accent"
        >
          <span className="block text-[14px] leading-tight font-bold text-fg">{item.name}</span>
          <span className="kicker mt-1.5 block normal-case">{item.note}</span>
        </li>
      ))}
    </ul>
  );
}
